import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ADMIN › KIR-STAT SEGÉDLET (backend/kirstat.js)
 *
 *  Az október 1-jei statisztikához (KIR-STAT, OSAP 1410) a rendszer adataiból számolt táblák,
 *  a KIR adatlapcsomagjához hasonló PDF-ben – a webes KIR-STAT kitöltéséhez. Itt adható meg a
 *  vonatkozási nap, a feladatellátási helyek és az, hogy melyik osztály melyik helyen tanul
 *  (a jogviszonyban ez nem szerepel).
 *
 *  OSAP-HATÁRIDŐ (229/2012. Korm. r. 25. § (3)): az október 1-jei állapotot október 15-ig kell
 *  beküldeni a KIR-STAT-ban. A „Beadtam” jelölés évenként rögzíti, ki és melyik napon adta be;
 *  amíg nincs jelölve, október 1–15. között naponta rendszerüzenet megy az adminnak.
 */
@Component({
  selector: 'app-admin-kirstat',
  templateUrl: './kirstat.component.html',
  styleUrls: ['./kirstat.component.scss'],
})
export class AdminKirstatComponent implements OnInit {
  intezmeny = '';
  datum = `${new Date().getMonth() + 1 >= 9 ? new Date().getFullYear() : new Date().getFullYear() - 1}-10-01`;
  helyek: any[] = [];
  osztalyok: any[] = [];
  segedlet: any = null;
  betolt = false;
  ment = false;
  pdfFut = false;
  hiba = '';

  beadas: any = null;
  beadasDatum = '';
  beadasMegj = '';
  beadasFut = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si: any = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await Promise.all([this.helyekBetolt(), this.beadasBetolt()]);
  }

  /** Az OSAP-beadás állapota – a vonatkozási nap éve szerint. */
  get beadasEv(): number { return Number(String(this.datum).slice(0, 4)) || new Date().getFullYear(); }

  async beadasBetolt() {
    try {
      this.beadas = await firstValueFrom(this.http.get(`/api/kirstat/beadas/${this.intezmeny}?ev=${this.beadasEv}`));
      const ma = new Date().toLocaleDateString('sv-SE');
      this.beadasDatum = this.beadas?.beadas_datuma || (ma < this.beadas?.vonatkozasi_nap ? this.beadas.vonatkozasi_nap : ma);
    } catch (e: any) { this.beadas = null; this.hiba = e?.error?.error || 'Az OSAP-beadás állapota nem tölthető be.'; }
  }

  async beadasJelol(beadva: boolean) {
    if (!beadva && !confirm('Biztosan visszavonod a „Beadtam” jelölést? A napi emlékeztető (október 1–15.) újraindul.')) return;
    this.beadasFut = true;
    try {
      this.beadas = await firstValueFrom(this.http.post(`/api/kirstat/beadas/${this.intezmeny}`, {
        ev: this.beadasEv, beadva, datum: this.beadasDatum, megjegyzes: this.beadasMegj }));
      this.beadasMegj = '';
      this.data.sendToast(beadva ? 'Rögzítve: a KIR-STAT be van adva.' : 'A jelölés visszavonva.', 'success');
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'A jelölés nem sikerült.', 'error'); }
    finally { this.beadasFut = false; }
  }

  beadasAllapotNev(a: string): string {
    return ({ beadva: 'Beadva', kesve_beadva: 'Beadva (a határidő után)', esedekes: 'Esedékes', lejart: 'Lejárt – nincs beadva jelölve',
      meg_nem_esedekes: 'Még nem esedékes' } as any)[a] || a;
  }

  async helyekBetolt() {
    this.hiba = '';
    try {
      const r: any = await firstValueFrom(this.http.get(`/api/kirstat/helyek/${this.intezmeny}?datum=${this.datum}`));
      this.helyek = r.helyek || [];
      this.osztalyok = r.osztalyok || [];
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült betölteni.'; }
  }

  ujHely() {
    const kodok = this.helyek.map((h) => Number(h.kod) || 0);
    const kod = String(Math.max(0, ...kodok) + 1).padStart(3, '0');
    this.helyek.push({ kod, nev: '', irsz: '', telepules: this.helyek[0]?.telepules || '', cim: '' });
  }
  helyTorol(i: number) {
    const kod = this.helyek[i].kod;
    this.helyek.splice(i, 1);
    for (const o of this.osztalyok) if (o.fh === kod) o.fh = this.helyek[0]?.kod;
  }

  async helyekMent() {
    this.ment = true; this.hiba = '';
    try {
      await firstValueFrom(this.http.put(`/api/kirstat/helyek/${this.intezmeny}`, {
        helyek: this.helyek, osztalyok: this.osztalyok.map((o) => ({ id: o.id, fh: o.fh })) }));
      this.data.sendToast('A feladatellátási helyek mentve.', 'success');
      await this.helyekBetolt();
      if (this.segedlet) await this.elonezet();
    } catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült menteni.'; }
    finally { this.ment = false; }
  }

  async elonezet() {
    this.betolt = true; this.hiba = '';
    try { this.segedlet = await firstValueFrom(this.http.get(`/api/kirstat/segedlet/${this.intezmeny}?datum=${this.datum}`)); }
    catch (e: any) { this.hiba = e?.error?.error || 'Nem sikerült a számolás.'; }
    finally { this.betolt = false; }
  }

  async pdf() {
    this.pdfFut = true;
    try { await this.data.megnyitFajl(`/api/kirstat/pdf/${this.intezmeny}?datum=${this.datum}`, `KIR-STAT segédlet ${this.datum}`); }
    finally { this.pdfFut = false; }
  }

  kitoltott(a: any): number { return (a?.tablak || []).filter((t: any) => t.allapot === 'Kitöltve').length; }
  ertek(t: any, s: number, o: number) { return t?.ertekek?.[s]?.[o] ?? ''; }
}
